import {
  mapCopyAssistResultDto,
  mapPlacementExpandResultDto,
  mapTextGenResultDto,
  type CopyAssistRequest,
  type CopyAssistResult,
  type CopyAssistResultDto,
  type PlacementExpandRequest,
  type PlacementExpandResult,
  type PlacementExpandResultDto,
  type TextGenRequest,
  type TextGenResult,
  type TextGenResultDto,
} from '@/types';

import type { CanvasAiService } from '../canvasAiService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`CanvasAiService`(ADR-026)
 * -----------------------------------------------------------------------------
 * - `POST /api/projects/{pid}/copy-assist`(200;允許部分成功)
 * - `POST /api/projects/{pid}/placement-expand`(201)
 *
 * 都走 `httpRequestWithRefresh`(cookie auth + CSRF + `X-Current-Group` 自動注入 +
 * 401 refresh chain)。`idempotency_key` 放 **request body**;BaseResponse `result`
 * unwrap,缺 result throw 500 MISSING_RESULT(對齊 httpDagService / httpCatalogService)。
 */

const PATH = {
  copyAssist: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/copy-assist`,
  placementExpand: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/placement-expand`,
  textGen: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/text-gen`,
} as const;

export function createHttpCanvasAiService(config: HttpClientConfig): CanvasAiService {
  return {
    async copyAssist(pid: string, req: CopyAssistRequest): Promise<CopyAssistResult> {
      const body: Record<string, unknown> = {
        source_node_ids: req.sourceNodeIds,
        angle_ids: req.angleIds,
        idempotency_key: req.idempotencyKey,
      };
      if (req.targetHint) body.target_hint = req.targetHint;
      // R424 — 語系有值才上 wire(舊 BE `extra="forbid"` → 422)。
      if (req.locale) body.locale = req.locale;
      const env = await httpRequestWithRefresh<CopyAssistResultDto>(
        config,
        'POST',
        PATH.copyAssist(pid),
        body,
      );
      return mapCopyAssistResultDto(unwrapResult(env, 'canvasAi.copyAssist'));
    },

    async placementExpand(
      pid: string,
      req: PlacementExpandRequest,
    ): Promise<PlacementExpandResult> {
      const body = {
        source_node_id: req.sourceNodeId,
        placement_id: req.placementId,
        idempotency_key: req.idempotencyKey,
      };
      const env = await httpRequestWithRefresh<PlacementExpandResultDto>(
        config,
        'POST',
        PATH.placementExpand(pid),
        body,
      );
      return mapPlacementExpandResultDto(unwrapResult(env, 'canvasAi.placementExpand'));
    },


    async textGen(pid: string, req: TextGenRequest): Promise<TextGenResult> {
      // 後端 path-scoped 讀 source COPY node config;body 只帶 source + idempotency_key
      //(extra=forbid;類別/則數不收 —— 後端一次全產,FE 生成後策展。docs/63 Q2)。
      const body: Record<string, unknown> = {
        source_node_id: req.sourceNodeId,
        idempotency_key: req.idempotencyKey,
      };
      // R424 — 語系有值才上 wire(舊 BE `extra="forbid"` → 422)。
      if (req.locale) body.locale = req.locale;
      const env = await httpRequestWithRefresh<TextGenResultDto>(
        config,
        'POST',
        PATH.textGen(pid),
        body,
      );
      return mapTextGenResultDto(unwrapResult(env, 'canvasAi.textGen'));
    },

  };
}
